import { useLanguage } from "@/contexts/language-context";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

const HELP_EMAIL = "help@evooluir.com";

/** Terms / Privacy from the same public GET /legal/:type the mobile app shows. */
export default function LegalPage({ type }: { type: "terms" | "privacy" }) {
  const { t } = useLanguage();
  const title = type === "terms" ? t.signup.termsLink : t.signup.privacyLink;
  const { data, isError } = useQuery({
    queryKey: ["legal", type],
    queryFn: () => api.get<{ html: string }>(`/legal/${type}`, true),
  });

  useEffect(() => {
    document.title = `${title} | Evooluir`;
  }, [title]);

  if (data?.html) {
    return (
      <iframe
        title={title}
        // base target: mailto/external links in the doc must leave the sandboxed frame
        srcDoc={data.html.replace("<head>", '<head><base target="_blank">')}
        // never allow-scripts/allow-same-origin: html is unsanitized and this origin holds auth_token
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        className="block w-full h-[100dvh] border-0"
      />
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3 px-6 text-center">
      {isError ? (
        <>
          <h1 className="font-serif text-3xl font-medium">{title}</h1>
          <p className="text-muted-foreground">
            {t.footer.contact}:{" "}
            <a href={`mailto:${HELP_EMAIL}`} className="text-[#FF68C3] hover:underline">
              {HELP_EMAIL}
            </a>
          </p>
        </>
      ) : (
        <Loader2 className="w-8 h-8 animate-spin text-[#FF68C3]" aria-label={title} />
      )}
    </div>
  );
}
